import React from "react";
import { Link } from "react-router-dom";
import { Building2, ShieldCheck, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import { MODULES } from "@/data/constants";
import { useI18n } from "@/context/I18nContext";
import { openCookiePreferences } from "@/components/CookieConsent";

export function Footer() {
  const { t, lang } = useI18n();
  const fr = lang !== "en";
  return (
    <footer className="relative border-t border-white/10 bg-[#050505] pt-16 pb-8" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-1">
            <Logo size={44} />
            <p className="mt-5 text-sm text-white/50 leading-relaxed">
              {t("footer.tagline")}
              <br />
              {t("footer.sub")}
            </p>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {t("footer.modules")}
            </h4>
            <ul className="space-y-2.5">
              {MODULES.map((m) => (
                <li key={m.key}>
                  <Link
                    to={m.key === "support" ? "/support" : `/module/${m.key}`}
                    className="text-sm text-white/50 hover:text-[#EAB308] transition-colors"
                  >
                    {m.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {t("footer.ecosystem")}
            </h4>
            <ul className="space-y-2.5 text-sm text-white/50">
              <li><a href="/#modules" className="hover:text-[#EAB308] transition-colors">{t("footer.seeModules")}</a></li>
              <li><a href="/#forfaits" className="hover:text-[#EAB308] transition-colors">{t("footer.plans")}</a></li>
              <li><a href="/#ecosysteme" className="hover:text-[#EAB308] transition-colors">{t("footer.ecoCentral")}</a></li>
              <li><a href="/#demo" className="hover:text-[#EAB308] transition-colors">{t("footer.demo")}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {t("footer.account")}
            </h4>
            <ul className="space-y-2.5 text-sm text-white/50">
              <li><a href="/login" className="hover:text-[#EAB308] transition-colors">{t("footer.login")}</a></li>
              <li><a href="/register" className="hover:text-[#EAB308] transition-colors">{t("footer.register")}</a></li>
              <li><a href="/app" className="hover:text-[#EAB308] transition-colors">{t("footer.dashboard")}</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-2.5">
            <Building2 className="w-5 h-5 text-white/50 mt-0.5" />
            <div>
              <p className="text-sm text-white/70">PROGÈRE — écosystème PROGÈRE ERP</p>
              <p className="text-xs text-white/50">
                Propulsé par <span className="text-[#EAB308] font-semibold">Groupe MJR Construction Inc.</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <Link to="/confidentialite" data-testid="footer-privacy" className="text-white/60 hover:text-[#EAB308] transition-colors">
              {t("footer.privacy")}
            </Link>
            <Link to="/conditions" data-testid="footer-terms" className="text-white/60 hover:text-[#EAB308] transition-colors">
              {t("footer.terms")}
            </Link>
            <Link to="/mentions-legales" data-testid="footer-notice" className="text-white/60 hover:text-[#EAB308] transition-colors">
              {t("footer.notice")}
            </Link>
            <button type="button" onClick={openCookiePreferences} data-testid="footer-cookie-prefs"
              className="text-white/60 hover:text-[#EAB308] transition-colors">
              {fr ? "Gérer mes témoins" : "Manage cookies"}
            </button>
            <Link to="/login" className="text-[#EAB308] hover:underline font-medium">{t("footer.connect")}</Link>
          </div>
        </div>

        {/* Conformité Loi 25 (Québec) */}
        <div className="mt-6 rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="footer-loi25">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 text-xs text-white/50">
            <span className="inline-flex items-center gap-1.5 text-white/70">
              <ShieldCheck className="w-4 h-4 text-[#EAB308]" />
              {fr ? "Conforme à la Loi 25 (Québec)" : "Compliant with Law 25 (Quebec)"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-white/40" />
              {fr ? "Données hébergées au Canada" : "Data hosted in Canada"}
            </span>
            <span className="md:ml-auto">
              {fr ? "Responsable de la protection des renseignements personnels : " : "Privacy officer: "}
              <a href="mailto:confidentialite@progere.ca" className="text-[#EAB308] hover:underline">confidentialite@progere.ca</a>
            </span>
          </div>
        </div>
        <p className="mt-6 text-xs text-white/30 font-mono">
          © {new Date().getFullYear()} PROGÈRE — progere.ca. {t("footer.rights")}
        </p>
      </div>
    </footer>
  );
}
